import React, { useState, useEffect } from 'react';
import { Button, Upload, Space, Table, Typography, message, Spin, Tooltip, Card, Modal, Tabs } from 'antd';
import { UploadOutlined, DownloadOutlined, CopyOutlined, InboxOutlined, BuildOutlined } from '@ant-design/icons';
import { RcFile, UploadFile } from 'antd/es/upload';
import type { ColumnsType } from 'antd/es/table';
import { parseAB1Files, AB1FileResult } from '@/api/sequencingService';
import SangerTraceThumbnail from '../charts/SangerTraceThumbnail';
import EnhancedSangerTraceChart from '../charts/EnhancedSangerTraceChart';
import SequenceAssembly from './SequenceAssembly';

const { Text } = Typography;
const { Dragger } = Upload;
const { TabPane } = Tabs;

interface SequenceDataItem {
  key: string;
  filename: string;
  sequence: string;
  length: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
}

const SangerSequenceAnalyzer: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<AB1FileResult[]>([]);
  const [isChartModalVisible, setIsChartModalVisible] = useState(false);
  const [selectedChartResult, setSelectedChartResult] = useState<AB1FileResult | null>(null);
  const [isAssemblyModalVisible, setIsAssemblyModalVisible] = useState(false);
  const [assemblyLoading, setAssemblyLoading] = useState(false);

  const handleShowChartModal = (result: AB1FileResult) => {
    setSelectedChartResult(result);
    setIsChartModalVisible(true);
  };

  const handleCloseChartModal = () => {
    setIsChartModalVisible(false);
    setSelectedChartResult(null);
  };

  const handleShowAssemblyModal = async () => {
    // 如果已经有解析结果，直接显示组装对话框
    if (results && results.length > 0) {
      setIsAssemblyModalVisible(true);
      return;
    }
    
    // 如果没有解析结果但有上传的文件，先解析文件
    if (fileList.length > 0) {
      setAssemblyLoading(true);
      try {
        const response = await parseAB1Files(fileList as unknown as File[]);
        if (response && response.length > 0) {
          setResults(response);
          message.success(`成功解析 ${response.length} 个文件`);
          setIsAssemblyModalVisible(true);
        } else {
          message.warning('未能解析任何文件');
        }
      } catch (error) {
        console.error('解析文件出错:', error);
        message.error('解析文件出错，请稍后重试');
      } finally {
        setAssemblyLoading(false);
      }
    } else {
      // 如果没有上传文件，直接显示组装对话框（用户可以输入FASTA序列）
      setIsAssemblyModalVisible(true);
    }
  };

  const handleCloseAssemblyModal = () => {
    setIsAssemblyModalVisible(false);
  };
  
  const handleClear = () => {
    if (fileList.length === 0 && results.length === 0) {
      message.info('没有需要清除的内容');
      return;
    }
    
    setFileList([]);
    setResults([]);
    message.success('已清除所有文件和解析结果');
  };
  
  const beforeUpload = (file: RcFile) => {
    const isAB1 = file.name.toLowerCase().endsWith('.ab1');
    if (!isAB1) {
      message.error('只支持上传AB1格式文件！');
      return false;
    }
    setFileList(prev => [...prev, file as UploadFile]);
    return false;
  };
  
  const handleRemove = (file: UploadFile) => {
    setFileList(fileList.filter(item => item.uid !== file.uid));
  };
  
  const handleParse = async () => {
    if (fileList.length === 0) {
      message.warning('请先选择AB1文件');
      return;
    }
    setLoading(true);
    try {
      const response = await parseAB1Files(fileList as unknown as File[]);
      if (response && response.length > 0) {
        setResults(response);
        message.success(`成功解析 ${response.length} 个文件`);
      } else {
        message.warning('未能解析任何文件');
      }
    } catch (error) {
      message.error('解析文件出错，请稍后重试');
      console.error('解析文件出错:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const copySequence = (sequence: string) => {
    navigator.clipboard.writeText(sequence)
      .then(() => message.success('序列已复制到剪贴板'))
      .catch(() => message.error('复制失败'));
  };
  
  const downloadFasta = (sequence: string, filename: string) => {
    const fastaName = filename.replace(/\.ab1$/i, '');
    const fastaContent = `>${fastaName}\n${sequence}`;
    const blob = new Blob([fastaContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fastaName}.fasta`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const downloadCsv = () => {
    if (results.length === 0) return;
    const headers = "文件名,序列长度,序列\n";
    const rows = results.map(item => `"${item.filename}",${item.data.sequence.length},"${item.data.sequence}"`).join('\n');
    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sanger_sequences.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 确保results存在且非空后再进行map操作
  const tableData: SequenceDataItem[] = results && results.length > 0 ? results.map((item, index) => {
    const highCount = item.data.confidence_levels.filter(level => level === 'high').length;
    const mediumCount = item.data.confidence_levels.filter(level => level === 'medium').length;
    const lowCount = item.data.confidence_levels.filter(level => level === 'low').length;
    
    return {
      key: item.filename,
      filename: item.filename,
      sequence: item.data.sequence,
      length: item.data.sequence.length,
      highConfidence: highCount,
      mediumConfidence: mediumCount,
      lowConfidence: lowCount
    };
  }) : [];

  const columns: ColumnsType<SequenceDataItem> = [
    { title: '文件名', dataIndex: 'filename', key: 'filename', width: 200 },
    {
      title: '峰图',
      key: 'thumbnail',
      width: 220,
      render: (_, record) => {
        const result = results.find(r => r.filename === record.filename);
        if (!result) return null;
        return <SangerTraceThumbnail result={result} onClick={() => handleShowChartModal(result)} />;
      },
    },
    { title: '长度', dataIndex: 'length', key: 'length', width: 80 },
    {
      title: '序列',
      dataIndex: 'sequence',
      key: 'sequence',
      ellipsis: true,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace' }}>{text}</span>
      )
    },
    {
      title: '碱基可信度',
      children: [
        {
          title: '高',
          dataIndex: 'highConfidence',
          key: 'highConfidence',
          width: 70,
          render: (value, record) => {
            const percent = record.length > 0 ? Math.round((value / record.length) * 100) : 0;
            return <Tooltip title={`${percent}%`}>{value}</Tooltip>;
          }
        },
        {
          title: '中',
          dataIndex: 'mediumConfidence',
          key: 'mediumConfidence',
          width: 70,
          render: (value, record) => {
            const percent = record.length > 0 ? Math.round((value / record.length) * 100) : 0;
            return <Tooltip title={`${percent}%`}>{value}</Tooltip>;
          }
        },
        {
          title: '低',
          dataIndex: 'lowConfidence',
          key: 'lowConfidence',
          width: 70,
          render: (value, record) => {
            const percent = record.length > 0 ? Math.round((value / record.length) * 100) : 0;
            return <Tooltip title={`${percent}%`}>{value}</Tooltip>;
          }
        }
      ]
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="复制序列">
            <Button icon={<CopyOutlined />} onClick={() => copySequence(record.sequence)} />
          </Tooltip>
          <Tooltip title="下载FASTA">
            <Button icon={<DownloadOutlined />} onClick={() => downloadFasta(record.sequence, record.filename)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <Spin spinning={loading || assemblyLoading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Dragger
            accept=".ab1"
            multiple
            beforeUpload={beforeUpload}
            onRemove={handleRemove}
            fileList={fileList}
            maxCount={20}
            style={{ padding: '20px 0' }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽AB1文件到此区域上传</p>
            <p className="ant-upload-hint">支持单个或批量上传，仅支持AB1格式文件</p>
          </Dragger>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Space>
              <Button type="primary" onClick={handleParse} disabled={fileList.length === 0}>
                解析文件
              </Button>
              <Button 
                type="primary" 
                icon={<BuildOutlined />} 
                onClick={handleShowAssemblyModal}
              >
                序列组装
              </Button>
              <Button 
                danger
                onClick={handleClear}
              >
                清除
              </Button>
            </Space>
          </div>
        </Card>

        {results && results.length > 0 && (
          <Card>
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button onClick={downloadCsv} icon={<DownloadOutlined />}>导出表格 (CSV)</Button>
            </div>
            <Table 
              columns={columns} 
              dataSource={tableData} 
              size="small"
              bordered
              rowKey="filename"
              scroll={{ y: 500 }}
            />
          </Card>
        )}
      </Space>

      {/* 峰图弹窗 */}
      {selectedChartResult && (
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>峰图: {selectedChartResult.filename}</span>
            </div>
          }
          open={isChartModalVisible}
          onCancel={handleCloseChartModal}
          footer={null}
          width="95%"
          style={{ top: 20 }}
          bodyStyle={{ 
            maxHeight: 'calc(95vh - 100px)', 
            minHeight: '700px',
            overflow: 'auto',
            padding: '20px' 
          }}
          destroyOnClose={true}
        >
          <EnhancedSangerTraceChart result={selectedChartResult} />
        </Modal>
      )}

      {/* 序列组装弹窗 */}
      <Modal
        title={<><BuildOutlined /> 序列组装</>}
        open={isAssemblyModalVisible}
        onCancel={handleCloseAssemblyModal}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        bodyStyle={{ 
          maxHeight: 'calc(95vh - 100px)', 
          overflow: 'auto',
          padding: '20px' 
        }}
        destroyOnClose={true}
      >
        <SequenceAssembly ab1Results={results} />
      </Modal>
    </Spin>
  );
};

export default SangerSequenceAnalyzer;